import { DataTable } from '@/components/data-table/DataTable';
import { PageHeader } from '@/components/feedback/PageHeader';
import { FillPage } from '@/components/layout/PageContainer';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { formatDate, formatDateTime } from '@/lib/utils/date';
import type { ColumnDef } from '@tanstack/react-table';
import {
  AudioLines,
  Code,
  FileDiff,
  RefreshCw
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTtsFlowLogs } from '../api';
import { buildLineDiff, LINE_CLASS, LINE_SIGN, MAX_DIFF_LINES, prettyJson } from '../lib/diff-helper';
import { ACTION_META, type TtsFlowLogAction, type TtsFlowLogRow } from '../types';

function ActionBadge({ action }: { action: TtsFlowLogAction }) {
  const meta = ACTION_META[action];
  const Icon = meta.icon;
  return (
    <Badge variant="outline" className={`gap-1 font-normal ${meta.className}`}>
      <Icon className="h-3 w-3" />
      {meta.label}
    </Badge>
  );
}

function JsonPane({
  title,
  tone,
  json,
}: {
  title: string;
  tone: 'added' | 'removed';
  json: string;
}) {
  return (
    <div className="min-w-0 space-y-1.5">
      <span
        className={`text-xs font-medium ${
          tone === 'removed'
            ? 'text-rose-600 dark:text-rose-400'
            : 'text-emerald-600 dark:text-emerald-400'
        }`}
      >
        {title}
      </span>
      <pre className="max-h-[55vh] overflow-auto rounded-md border border-border bg-muted/40 p-3 font-mono text-xs leading-relaxed">
        {json}
      </pre>
    </div>
  );
}

function ChangeViewerDialog({ row, onClose }: { row: TtsFlowLogRow | null; onClose: () => void }) {
  const [mode, setMode] = useState<'unified' | 'split'>('unified');

  const isUpdate = row?.action === 'update';
  const oldText = prettyJson(row?.oldTtsFlowJson);
  const newText = prettyJson(row?.newTtsFlowJson);
  const canDiff = isUpdate && oldText !== '' && newText !== '';

  const diff = useMemo(() => {
    if (!canDiff) return null;
    if (oldText.split('\n').length + newText.split('\n').length > MAX_DIFF_LINES) return null;
    return buildLineDiff(oldText, newText);
  }, [canDiff, oldText, newText]);

  const added = diff?.filter((l) => l.type === 'added').length ?? 0;
  const removed = diff?.filter((l) => l.type === 'removed').length ?? 0;

  // Single-payload fallback: delete stores old, everything else stores new.
  const singleText = row
    ? prettyJson(
        row.action === 'delete'
          ? (row.oldTtsFlowJson ?? row.newTtsFlowJson)
          : (row.newTtsFlowJson ?? row.oldTtsFlowJson),
      )
    : '';
  const singleLabel = !row
    ? ''
    : row.action === 'create'
      ? 'Created record'
      : row.action === 'delete'
        ? 'Record before deletion'
        : row.action === 'update'
          ? row.newTtsFlowJson
            ? 'Record after update'
            : 'Record before update'
          : `Record snapshot at ${row.action === 'approve' ? 'approval' : 'rejection'}`;

  return (
    <Dialog open={row !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl lg:max-w-5xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {row && <ActionBadge action={row.action} />}
            Change — log #{row?.id}
          </DialogTitle>
          <DialogDescription>
            Flow #{row?.ttsFlowId} · {row ? formatDateTime(row.createdAt) : ''} · user{' '}
            {row?.userId ?? '—'}
          </DialogDescription>
        </DialogHeader>

        {canDiff ? (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-xs text-muted-foreground">
                <span className="font-medium text-emerald-600 dark:text-emerald-400">
                  +{added}
                </span>{' '}
                <span className="font-medium text-rose-600 dark:text-rose-400">−{removed}</span>{' '}
                line(s) changed
              </span>
              {diff && (
                <div className="flex items-center gap-0.5 rounded-md border border-border p-0.5">
                  <Button
                    size="sm"
                    variant={mode === 'unified' ? 'secondary' : 'ghost'}
                    className="h-7 px-2.5 text-xs"
                    onClick={() => setMode('unified')}
                  >
                    Diff
                  </Button>
                  <Button
                    size="sm"
                    variant={mode === 'split' ? 'secondary' : 'ghost'}
                    className="h-7 px-2.5 text-xs"
                    onClick={() => setMode('split')}
                  >
                    Side by side
                  </Button>
                </div>
              )}
            </div>

            {diff && mode === 'unified' ? (
              <div className="max-h-[55vh] overflow-auto rounded-md border border-border bg-muted/30 font-mono text-xs leading-relaxed">
                {diff.map((line, idx) => (
                  <div key={idx} className={`px-3 py-0.5 ${LINE_CLASS[line.type]}`}>
                    <span className="select-none pr-2 opacity-60">{LINE_SIGN[line.type]}</span>
                    <span className="whitespace-pre-wrap break-all">{line.text}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid gap-3 md:grid-cols-2">
                <JsonPane title="Before" tone="removed" json={oldText} />
                <JsonPane title="After" tone="added" json={newText} />
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            <span className="text-xs font-medium text-muted-foreground">{singleLabel}</span>
            <pre className="max-h-[60vh] overflow-auto rounded-md border border-border bg-muted/40 p-3 font-mono text-xs leading-relaxed">
              {singleText}
            </pre>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function TtsLogsPage() {
  const today = useMemo(() => new Date(), []);
  const [startDate, setStartDate] = useState<Date | undefined>(today);
  const [endDate, setEndDate] = useState<Date | undefined>(today);

  const startStr = startDate ? formatDate(startDate) : '';
  const endStr = endDate ? formatDate(endDate) : '';

  const logs = useTtsFlowLogs(startStr, endStr);
  const rows = useMemo(
    () => [...(logs.data ?? [])].sort((a, b) => b.id - a.id),
    [logs.data],
  );

  const [viewing, setViewing] = useState<TtsFlowLogRow | null>(null);

  const columns = useMemo<ColumnDef<TtsFlowLogRow>[]>(
    () => [
      {
        accessorKey: 'id',
        header: 'ID',
        size: 90,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">{row.original.id}</span>
        ),
      },
      {
        accessorKey: 'ttsFlowId',
        header: 'Flow',
        cell: ({ row }) => (
          <span className="flex items-center gap-2 font-medium text-foreground">
            <AudioLines className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="font-mono text-xs">#{row.original.ttsFlowId}</span>
          </span>
        ),
      },
      {
        accessorKey: 'action',
        header: 'Action',
        size: 120,
        cell: ({ row }) => <ActionBadge action={row.original.action} />,
      },
      {
        accessorKey: 'userId',
        header: 'User',
        size: 110,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">
            {row.original.userId ?? '—'}
          </span>
        ),
      },
      {
        accessorKey: 'createdAt',
        header: 'When',
        size: 190,
        cell: ({ row }) => formatDateTime(row.original.createdAt),
      },
      {
        id: 'change',
        header: 'Change',
        size: 110,
        enableSorting: false,
        cell: ({ row }) => {
          const r = row.original;
          const isDiff =
            r.action === 'update' &&
            r.oldTtsFlowJson != null &&
            r.newTtsFlowJson != null;
          const hasPayload = r.oldTtsFlowJson != null || r.newTtsFlowJson != null;
          if (!hasPayload) {
            return <span className="text-xs text-muted-foreground">—</span>;
          }
          return (
            <Button size="sm" variant="outline" onClick={() => setViewing(r)}>
              {isDiff ? <FileDiff className="h-3.5 w-3.5" /> : <Code className="h-3.5 w-3.5" />}
              {isDiff ? 'Diff' : 'View'}
            </Button>
          );
        },
      },
    ],
    [],
  );

  return (
    <FillPage>
      <PageHeader
        title="Change History"
        description="Audit trail for every TTS flow change — creates, updates, deletes, approvals and rejections."
        actions={
          <Button variant="outline" onClick={() => logs.refetch()} disabled={logs.isFetching}>
            <RefreshCw className={logs.isFetching ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
            Refresh
          </Button>
        }
      />

      <div className="flex min-h-0 flex-1 flex-col gap-3">
        {/* Date filter bar */}
        <div className="flex flex-wrap items-end gap-3 rounded-lg border border-border bg-card p-3 shadow-sm">
          <div className="space-y-1.5">
            <Label className="text-xs">Start date</Label>
            <DatePicker value={startDate} onChange={setStartDate} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">End date</Label>
            <DatePicker value={endDate} onChange={setEndDate} />
          </div>
          <span className="ml-auto text-xs text-muted-foreground">
            {rows.length.toLocaleString()} {rows.length === 1 ? 'log entry' : 'log entries'}
          </span>
        </div>

        <div className="min-h-0 flex-1">
          <DataTable<TtsFlowLogRow, unknown>
            tableId="tts-flow:audit-logs:v1"
            columns={columns}
            data={rows}
            isLoading={logs.isLoading}
            searchPlaceholder="Search logs…"
            emptyTitle="No change history"
            emptyDescription="TTS flow changes in this date range will appear here."
            getRowId={(row) => String(row.id)}
            initialColumnPinning={{ right: ['change'] }}
            exportFilename="tts-flow-logs.csv"
            pageSize={25}
          />
        </div>
      </div>

      <ChangeViewerDialog row={viewing} onClose={() => setViewing(null)} />
    </FillPage>
  );
}